import { useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import type { InspectionTemplate } from 'inspection-types';
import {
  useForkInspectionTemplate,
  useHideInspectionTemplate,
  useUnhideInspectionTemplate,
} from 'react-hooks';
import { SourceType } from 'graphql-query-contracts';

import { isTemplateOwnedByChannel } from '../helpers/isTemplateOwnedByChannel';
import type { InspectionsChannel } from '../types/channel';

/** Undo window for the hide/unhide toast — longer than the shared Toast's 2500ms default. */
export const UNDO_TOAST_DURATION_MS = 5000;

export type VisibilityChangedResult = {
  templateId: string;
  hidden: boolean;
  forkedFromTemplateId?: string;
};

export type ToggleCompleteResult = {
  templateName: string;
  hidden: boolean;
  /**
   * Reverts the visibility change. Takes the `onVisibilityChanged` callback as
   * an argument (rather than closing over the version captured when the toggle
   * ran) so the caller supplies the current one at click time — no stale
   * closure. It must report the *reverse* of the toggle to re-sync the list,
   * and it deliberately does not resolve a new completion, so it can't spawn an
   * undo-the-undo toast.
   */
  undo: (
    onVisibilityChanged: (result: VisibilityChangedResult) => Promise<void>
  ) => Promise<void>;
};

type UseToggleInspectionTemplateVisibilityArgs = {
  channel: InspectionsChannel;
  /**
   * Called whenever the effective visibility of a template changes and the
   * caller's list needs to re-sync — that's both the initial toggle AND a
   * later undo. Awaited, so the refreshed row is on screen before the loading
   * modal closes. The caller's own optimistic state is what should drive the
   * UI; the refetch only reconciles with the server.
   */
  onVisibilityChanged: (result: VisibilityChangedResult) => Promise<void>;
};

type UseToggleInspectionTemplateVisibilityResult = {
  /**
   * Resolves with the completion details (display name, resulting `hidden`
   * state, and an `undo` handle) once the toggle finishes and the loading
   * modal has closed — the caller uses this to surface the undo toast.
   * Resolves with `undefined` when nothing completed (cancelled confirm,
   * de-duped concurrent call, or a failed mutation). Undo deliberately does
   * NOT resolve a new completion, so it can't spawn an undo-the-undo toast.
   */
  toggleTemplateVisibility: (
    template: InspectionTemplate
  ) => Promise<ToggleCompleteResult | undefined>;
};

/**
 * Orchestrates hiding/unhiding a template from the Template Library.
 *
 * Flow:
 *  - Changing visibility requires the channel to own the template (the
 *    mutations validate building ownership server-side). If it isn't owned
 *    yet, this forks a building-owned copy first, then hides/unhides that
 *    copy. This applies to both directions: unhiding a foreign (parent)
 *    template directly would make it visible for every channel using it, so
 *    the fork keeps the change local. In the common case a hidden template is
 *    already an owned fork, so unhide won't re-fork it.
 *  - Both directions are confirmed first, with direction-specific copy —
 *    hiding removes the template from every mobile inspector at this
 *    property, and making it visible puts it back in front of them.
 *  - On success, the list is refetched and awaited before the loading modal
 *    closes, so the row's new state is already on screen — no page refresh
 *    needed. On error, the loading modal is dismissed and a toast shown
 *    without refetching.
 *  - Concurrent calls are de-duped via an in-flight guard.
 */
export function useToggleInspectionTemplateVisibility({
  channel,
  onVisibilityChanged,
}: UseToggleInspectionTemplateVisibilityArgs): UseToggleInspectionTemplateVisibilityResult {
  const { t } = useTranslation();
  const { forkInspectionTemplate } = useForkInspectionTemplate();
  const { hideInspectionTemplate } = useHideInspectionTemplate();
  const { unhideInspectionTemplate } = useUnhideInspectionTemplate();
  const inFlightRef = useRef(false);

  const toggleTemplateVisibility = useCallback(
    async (template: InspectionTemplate) => {
      if (inFlightRef.current) {
        return;
      }

      // Set before the (async) confirm dialog too, so a second click while
      // the dialog is still open doesn't stack a duplicate confirm.
      inFlightRef.current = true;

      const nextHidden = !template.hidden;

      // Both directions confirm first: hiding removes the template from every
      // mobile inspector at this property, and making it visible puts it back
      // in front of them.
      try {
        await window.Alert.confirm({
          title: t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideConfirmTitle'
              : 'web.admin.inspections.templateLibrary.visibility.unhideConfirmTitle'
          ),
          message: t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideConfirmMessage'
              : 'web.admin.inspections.templateLibrary.visibility.unhideConfirmMessage'
          ),
          confirmText: t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideConfirmButton'
              : 'web.admin.inspections.templateLibrary.visibility.unhideConfirmButton'
          ),
          cancelText: t(
            'web.admin.inspections.templateLibrary.visibility.cancelButton'
          ),
        });
      } catch {
        // Alert.confirm rejects when the user cancels — nothing to do.
        inFlightRef.current = false;

        return;
      }

      try {
        window.Alert.loading({
          title: t('web.admin.inspections.templateLibrary.visibility.preparing'),
          disableCloseOnBackgroundClick: true,
        });

        let targetTemplateId = template.id;
        let forkedFromTemplateId: string | undefined;

        if (!isTemplateOwnedByChannel(template, channel)) {
          const buildingEntityType = 'building';

          const { templateId } = await forkInspectionTemplate({
            parentTemplateId: template.id,
            buildingId: {
              entityId: channel._id,
              entityType: buildingEntityType,
              sourceType: SourceType.SourceTypeActivate,
            },
          });

          forkedFromTemplateId = template.id;
          targetTemplateId = templateId;
        }

        if (nextHidden) {
          await hideInspectionTemplate({
            channelId: channel._id,
            templateId: targetTemplateId,
          });
        } else {
          await unhideInspectionTemplate({
            channelId: channel._id,
            templateId: targetTemplateId,
          });
        }

        // The mutation succeeding is the source of truth for this row — pass
        // it on directly rather than waiting to see what the list refetch
        // reports, since that read model can lag the write. Still await the
        // refetch itself before dismissing the loading modal.
        await onVisibilityChanged({ templateId: targetTemplateId, hidden: nextHidden, forkedFromTemplateId });

        window.Alert.hide();

        const templateName = template.metadata.name;
        let undone = false;

        const undo = async (
          reportVisibilityChange: (
            result: VisibilityChangedResult
          ) => Promise<void>
        ) => {
          // The server may reject a redundant visibility transition, so Undo
          // fires at most once.
          if (undone) {
            return;
          }

          undone = true;

          try {
            if (nextHidden) {
              await unhideInspectionTemplate({
                channelId: channel._id,
                templateId: targetTemplateId,
              });
            } else {
              await hideInspectionTemplate({
                channelId: channel._id,
                templateId: targetTemplateId,
              });
            }

            // Report through the callback the caller hands in at click time,
            // not one captured when the toggle ran — avoids a stale closure.
            await reportVisibilityChange({
              templateId: targetTemplateId,
              hidden: !nextHidden,
            });
          } catch {
            // Surface the failure to the user; the row stays in its post-toggle
            // state. Uses the shared global toast (no action, no undo concern).
            await window.Toast.show(
              t('web.admin.inspections.templateLibrary.visibility.undoErrorToast')
            );

            throw new Error('undo-failed');
          }
        };

        // Hand completion back to the caller instead of via a second
        // callback: the toggle has fully settled here (list re-synced, modal
        // closed), so its resolved value is the natural place to surface the
        // undo affordance. `undo` takes the re-sync callback as an argument
        // (supplied fresh at click time) and never resolves another
        // completion, so it can't loop.
        return {
          templateName,
          hidden: nextHidden,
          undo,
        };
      } catch {
        window.Alert.hide();
        await window.Toast.show(
          t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideErrorToast'
              : 'web.admin.inspections.templateLibrary.visibility.unhideErrorToast'
          )
        );

        return undefined;
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      channel,
      forkInspectionTemplate,
      hideInspectionTemplate,
      onVisibilityChanged,
      t,
      unhideInspectionTemplate,
    ]
  );

  return { toggleTemplateVisibility };
}
